import * as React from "react";
import ImageComponent from "@project-stories/elements/Image/Image";
import {getImageUrl} from "@src/stories/components/Tile/TileMethods";

// Helper function to extract Vimeo video ID from URL
const extractVimeoId = (url: string): string | null => {
  if (!url) return null;
  
  const regex = /(?:vimeo\.com\/(?:.*#|.*\/videos\/|.*\/)?([0-9]+))/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

// Helper function to create Vimeo embed URL
const createVimeoEmbedUrl = (videoId: string, quality: string = "720", muted: boolean = true): string => {
  return `https://player.vimeo.com/video/${videoId}?autoplay=1&quality=${quality}p&loop=1&autopause=0&background=1&muted=${muted ? 1 : 0}`;
};

// Helper function to normalise aspect ratio value for CSS
const normalizeAspectRatio = (value: string | number | undefined | null): string | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  const str = String(value).trim();
  if (/^\d+(\.\d+)?\/\d+(\.\d+)?$/.test(str)) return str;
  if (/^\d+(\.\d+)?:\d+(\.\d+)?$/.test(str)) return str.replace(":", "/");
  if (/^\d+(\.\d+)?$/.test(str)) return str;
  return undefined;
};

const TileImage = ({ data, height, isMobile, priority = false }) => {
    const imageUrl = getImageUrl(data, isMobile);
    const vimeoUrl = isMobile 
        ? data.settings.vimeo_link_mobile || data.settings.vimeo_link
        : data.settings.vimeo_link;
    const rawAspectRatio = isMobile ? data.settings.video_aspect_ratio_mobile || data.settings.video_aspect_ratio : data.settings.video_aspect_ratio;
    const videoAspectRatio = normalizeAspectRatio(rawAspectRatio);

    let heightStyle = "auto";
    if (parseInt(height) > 0){
        heightStyle = height;
    }

    const vimeoId = extractVimeoId(vimeoUrl);
    const hasVimeoVideo = vimeoId !== null;
    const vimeoEmbedUrl = hasVimeoVideo ? createVimeoEmbedUrl(vimeoId, "720") : undefined;

    return (
        <>
            {(data.settings.image || hasVimeoVideo) && (
                <div
                    className={`image-container overflow-hidden relative`}
                    style={{ height: heightStyle }}
                >
                    {hasVimeoVideo ? (
                        // Render Vimeo video if URL is provided
                        <div className="w-full h-full relative"
                            style={{
                                aspectRatio: videoAspectRatio
                            }}
                        >
                            <iframe
                                src={vimeoEmbedUrl}
                                className={`absolute top-1/2 left-1/2 min-w-full min-h-full w-[300%] h-[300%] -translate-x-1/2 -translate-y-1/2 object-cover`}
                                style={{
                                    borderRadius: data.settings.media_rounding
                                }}
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                    ) : data.settings.image ? (
                        // Fallback to image if no Vimeo URL
                        <ImageComponent
                            imageUrl={imageUrl}
                            alt={data.settings.title || 'Tile image'}
                            classes={`object-cover h-full w-full`}
                            style={{ borderRadius: data.settings.media_rounding }}
                            isLazy={!priority}
                            useSrcSet={true}
                            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 75vw"
                            priority={priority}
                            containerClasses={`w-full ${data.settings.darken_image && "content-over-media"} relative`}
                        />
                    ) : null}
                </div>
            )}
        </>
    );
};

export default TileImage;
